import {CssBaseline} from '@mui/material';
import {ThemeProvider} from '@mui/material/styles';
import {useState} from 'react';
import {FullScreenLoading} from '../common/Loading';
import {ComponentWithRequiredChildren, PropsWithRequiredChildren} from '../react/types';
import {useOptionalCurrentUser} from '../users/hooks/useOptionalCurrentUser';
import {useUserPreferences} from '../users/hooks/useUserPreferences';
import {useSystemTheme} from './hooks/useSystemTheme';
import {useTheme} from './hooks/useTheme';
import {ThemeNameContext} from './hooks/useThemeName';
import {DefaultThemeName, themes} from './themes';
import {SystemTheme, ThemeName} from '#common/theme/themes';

export const CurrentThemeProvider: ComponentWithRequiredChildren = ({children}: PropsWithRequiredChildren) => {
  const maybeCurrentUser = useOptionalCurrentUser();

  if (maybeCurrentUser) {
    return <UserThemeProvider>{children}</UserThemeProvider>;
  }

  return <SimpleThemeProvider>{children}</SimpleThemeProvider>;
};

/* v8 ignore start */
interface SimpleThemeProviderProps extends PropsWithRequiredChildren {
  theme?: ThemeName;
}
export const SimpleThemeProvider: ComponentWithRequiredChildren<SimpleThemeProviderProps> = ({
  children,
  theme = DefaultThemeName,
}: SimpleThemeProviderProps) => {
  const [themeName, setThemeName] = useState<ThemeName>(theme);
  const systemTheme = useSystemTheme();
  const resolvedThemeName = themeName === SystemTheme ? systemTheme : themeName;
  return (
    <ThemeNameContext value={{setThemeName, themeName: resolvedThemeName}}>
      <ThemeProvider theme={themes[resolvedThemeName]}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </ThemeNameContext>
  );
};
/* v8 ignore stop */

const UserThemeProvider: ComponentWithRequiredChildren = ({children}: PropsWithRequiredChildren) => {
  const systemTheme = useSystemTheme();
  const {theme: userTheme} = useUserPreferences();
  const [themeName, setThemeName] = useState<ThemeName | undefined>(userTheme);
  const themeNameOrDefault = themeName ?? SystemTheme;
  const resolvedThemeName = themeNameOrDefault === SystemTheme ? systemTheme : themeNameOrDefault;
  const theme = useTheme(resolvedThemeName);
  if (!theme) {
    return <FullScreenLoading />;
  }
  return (
    <ThemeNameContext value={{setThemeName, themeName: resolvedThemeName}}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </ThemeNameContext>
  );
};
